import { Box, Fade, Modal, Skeleton, Stack } from '@mui/material'
import { AlreadyAccount } from 'Components/Elements/SignUp/SignUp'
import storeAuth from 'GlobalStores/User/StoreAuth'
import { observer } from 'mobx-react-lite'
import { Suspense, useEffect, useState } from 'react'
import { lazy } from 'react'
import pMinDelay from 'p-min-delay'
import styled from '@emotion/styled'
import Login from 'Components/Elements/Login/Login'
import StoreCountry from 'GlobalStores/Cores/County/StoreCountry'
import StoreReferral from 'GlobalStores/Referral/StoreReferral'
import StoreLoader from 'GlobalStores/Cores/Elements/StoreLoader'
import { useSizeWindow } from 'Helpers/GlobalHelpers'
import { DefaultButton } from 'Components/Elements/Forms'
import { useTranslate } from 'GlobalStores/Cores/Translator/useTranslate'
import MoneyBack from 'Components/Elements/Checkout/MoneyBack'
import storeCheckout from 'GlobalStores/Checkout/StoreCheckout'
import storeFormat from 'GlobalStores/Format/StoreFormat'
import { storeElement } from 'GlobalStores/StoreElement'
import { runInAction } from 'mobx'
import StorePaymentStep from 'GlobalStores/Cores/Elements/StorePaymentStep'
import { get } from 'lodash'
import { useNavigate } from 'react-router-dom'
import { FeelGreatAPI } from 'Services/FeelGreat/APIs'
import ReCaptchaV3 from 'Components/Elements/ReCaptcha/ReCaptchaV3'
import axios from 'axios'
import HttpHelper from 'Services/HttpHelper'

const PreSummaryBoxLazyLoad = lazy(() => pMinDelay(import('../../Elements/Checkout/PreSummaryBox'), 0))

const SaudiarabiaShippingAddressLazyLoad = lazy(() =>
    pMinDelay(import('../../Elements/Checkout/ShippingAddressForm/Country/Saudiarabia/SaudiarabiaShippingAddress'), 0)
)

const PaymentBoxLazyLoad = lazy(() => pMinDelay(import('../../Elements/Checkout/Payment'), 0))
const PaymentReviewLazyLoad = lazy(() => pMinDelay(import('../../Elements/Checkout/PaymentReview'), 0))
const CreateAccountWithShippingLazyLoad = lazy(() =>
    pMinDelay(import('../../Elements/Checkout/ShippingAddressForm/Country/Saudiarabia/SaudiarabiaCreateAccountWithShipping'), 0)
)

const SaudiarabiaCheckoutTheme = observer(({ Sections, step, onPlaceOrder, openLoginModal, setOpenLoginModal }) => {
    const { width } = useSizeWindow()
    const [sectionReady, setSectionReady] = useState(false)
    const [loading, setLoading] = useState(false)

    const notShowLogin = () => {
        return ['JPN'].includes(StoreCountry.Country3()) && StoreReferral.HasReferral()
    }

    const fetchAddressListOptions = async () => {
        setLoading(true)
        const S3_URL = `https://s3.ap-southeast-1.amazonaws.com/temp.ushop.unicity.com/SaudiarabiaAddressData.json?v=${new Date().getTime()}`
        // const headRes = await axios.head(S3_URL)
        // const lastModified = new Date(headRes.headers['last-modified']).getTime()
        // const urlWithVersion = `${S3_URL}?v=${lastModified}`
        
        try {
            const proxyUrl = HttpHelper.Proxy({ originalUrl: S3_URL })
            
            const response = await axios.get(proxyUrl, {
                headers: {
                    'Cache-Control': 'no-cache'
                }
            })
            
            runInAction(() => {
                storeCheckout.CheckOutInstance().Instance().SetAddressListOptions(response.data)
            })
            setLoading(false)
        } catch (error) {
            console.error('Error fetching JSON:', error)
            setLoading(false)
        }
    }

    useEffect(() => {
        fetchAddressListOptions()
    }, [])

    const ThemeSections = step => {
        switch (step) {
            case 'payment':
                return <PaymentBoxLazyLoad onPlaceOrder={onPlaceOrder} />
            case 'review':
                return <PaymentReviewLazyLoad />
            case 'create-account':
                return <CreateAccountWithShippingLazyLoad />
            default:
                return <SaudiarabiaShippingAddressLazyLoad sectionReady={setSectionReady} />
        }
    }

    useEffect(() => {
        if (StoreLoader.IsFormatApiLoading() === true) {
            setSectionReady(false)
        }
    }, [StoreLoader.IsFormatApiLoading()])

    return (
        <>
            <Suspense fallback={<div></div>}>
                <Stack>{ThemeSections(step)}</Stack>
            </Suspense>

            <Stack width={width > 1200 ? '380px' : 'auto'}>
                {!sectionReady && StoreLoader.IsFormatApiLoading() ? (
                    <PageSkeletonSummaryElement />
                ) : (
                    <Suspense fallback={<PageSkeletonSummaryElement />}>
                        <PreSummaryBoxLazyLoad step={step} edit={true} isReady={storeCheckout.isReadyToPlaceOrder} onPlaceOrder={onPlaceOrder} />
                    </Suspense>
                )}
            </Stack>
            {step === 'create-account' && (
                <>
                    <DesktopAlreadyAccount>{!notShowLogin() && <AlreadyAccount setOpenLoginModal={setOpenLoginModal} />}</DesktopAlreadyAccount>

                    <Modal
                        open={openLoginModal}
                        onClose={() => {
                            if (storeAuth.loadingLogin) return
                            setOpenLoginModal(false)
                        }}
                        aria-labelledby="modal-modal-title"
                        aria-describedby="modal-modal-description">
                        <Fade in={openLoginModal}>
                            <Box
                                sx={{
                                    position: 'absolute',
                                    top: 'calc(50% - 40px)',
                                    left: '50%',
                                    transform: 'translate(-50%, -50%)',
                                    padding: '0px'
                                }}>
                                <Login showFooter={false} setOpenLoginModal={setOpenLoginModal} />
                            </Box>
                        </Fade>
                    </Modal>
                </>
            )}
        </>
    )
})

const PageSkeletonSummaryElement = observer(props => {
    return (
        <Stack>
            <Skeleton
                variant="rounded"
                width={'100%'}
                sx={{
                    margin: '0',
                    borderRadius: '12px',
                    height: '108px',
                    '@media screen and (min-width: 1200px)': {
                        height: '370px'
                    }
                }}
            />
        </Stack>
    )
})

const StackStyled = styled.div`
    padding-top: 40px;
    padding-bottom: 40px;
    min-height: 50vh;
    display: flex;
    flex-direction: ${props => (props.stepPaymentOrReview ? 'column' : 'column-reverse')};
    justify-content: ${props => (props.stepPaymentOrReview ? 'flex-start' : 'flex-end')};
    gap: 20px;

    @media screen and (min-width: 399px) {
        padding-top: 40px;
    }

    @media screen and (min-width: 600px) {
        padding-top: 60px;
    }

    @media screen and (min-width: 1200px) {
        display: grid;
        grid-template-columns: 770px 370px;
        gap: 22px;
        padding-top: 40px;
    }
`

const Container = styled(StackStyled)`
    flex-direction: ${props => (props.stepPaymentOrReview === 'payment' ? 'column' : 'column-reverse')};
    justify-content: ${props => (props.stepPaymentOrReview ? 'flex-start' : 'flex-end')};

    @media screen and (min-width: 768px) {
        padding-top: 40px;
        /* flex-direction: row;
        justify-content: center; */
        gap: 20px;
    }
`

const DesktopAlreadyAccount = styled('div')`
    display: block;
    @media screen and (min-width: 1200px) {
        display: none;
    }
`

export default SaudiarabiaCheckoutTheme
